import { shouldDisplayReviews } from '@/helpers';
import { trackMpEvent } from '@/helpers/analyticsTS';
import { SearchType } from '@/hooks/search/useSearch.helpers';
import { BaseSearchManagerOutput } from '@/hooks/search/useSearch.types';
import { DealsSearchState } from '@/reducers/dealsSearchReducer';
import { Tracking } from '@/types/analytics';
import { FILTER_ID, UrlSearchState } from '@/types/state/search';
import { CAMPAIGNS_SHOWN } from '../searchV2/constants/tracking';
import {
  getSearchFilters,
  getSearchLocationCity,
  getSearchLocationFilter,
  getSortedBy,
} from '../searchV2/helpers/tracking';
import { CampaignsShown, SearchContext, TrackingProps } from '../searchV2/types/tracking';
import { DealSearchItem, SistaminutenSearchItem } from './types/dealSearch';

function getCampaignsShown(type: SearchType, state: DealsSearchState): CampaignsShown | undefined {
  const campaigns = [];

  if (type === 'deals_preview') {
    const hasSistaminutenResults = (state.preview.sections || []).some(
      (section) => section.type === 'sistaminuten' && section.items.length > 0,
    );
    const hasDealsResults = (state.preview.sections || []).some(
      (section) => section.type === 'deals' && section.items.length > 0,
    );

    const hasBrandDealsResults = (state.preview.sections || []).some(
      (section) => section.type === 'brand_deals' && section.items.length > 0,
    );

    if (hasSistaminutenResults) {
      campaigns.push(CAMPAIGNS_SHOWN.SISTAMINUTEN);
    }

    if (hasDealsResults) {
      campaigns.push(CAMPAIGNS_SHOWN.DEALS);
    }

    if (hasBrandDealsResults) {
      campaigns.push(CAMPAIGNS_SHOWN.BRAND_DEALS);
    }
  } else {
    const hasDealsResults =
      type === 'deals' && state.list.results.some((result) => result.type === 'organic' && result.items.length > 0);
    const hasSistaminutenResults =
      type === 'deals_sistaminuten' &&
      state.list.results.some((result) => result.type === 'organic' && result.items.length > 0);
    const hasBrandDeals =
      type === 'brand_deals' &&
      state.list.results.some((result) => result.type === 'organic' && result.items.length > 0);

    const hasSponsored = state.list.results.some(
      (result) => result.type === 'sponsored' && result.section?.items?.length > 0,
    );

    if (hasDealsResults) {
      campaigns.push(CAMPAIGNS_SHOWN.DEALS);
    }

    if (hasSistaminutenResults) {
      campaigns.push(CAMPAIGNS_SHOWN.SISTAMINUTEN);
    }

    if (hasBrandDeals || hasSponsored) {
      campaigns.push(CAMPAIGNS_SHOWN.BRAND_DEALS);
    }
  }

  return campaigns.length > 0 ? campaigns : undefined;
}

export function trackSerpCardClick({
  searchManager,
  item,
  state,
  screenName,
}: {
  searchManager: BaseSearchManagerOutput;
  item: { type: 'deal'; data: DealSearchItem } | { type: 'sistaminuten'; data: SistaminutenSearchItem };
  state: DealsSearchState;
  screenName: Tracking.ScreenName;
}) {
  const properties: Tracking.CompanyClicked['properties'] = {
    screen_name: screenName,
    // nbr_search_results: searchManager.results > 0 ? searchManager.results : undefined,
    search_location_city: getSearchLocationCity(searchManager),
    search_location_filter: getSearchLocationFilter(searchManager),
    campaigns_shown: getCampaignsShown(searchManager.type, state),
    search_query: searchManager?.q.trim?.().length > 0 ? searchManager.q : undefined,
  };

  if (item.type === 'sistaminuten') {
    if (shouldDisplayReviews(item) && item.data?.rating?.count > 4) {
      properties['ratings_average'] = +item.data.rating.score.toFixed(1);
      properties['number_of_ratings'] = item.data.rating.count.toString();
    }
    properties['marketplace_id'] = item.data.id;
    properties['salon_id'] = item.data.portalId;
    properties['search_variant'] = 'sistaminuten';

    trackMpEvent({ name: 'company_clicked', properties });
  }
}

const SISTAMINUTEN_FILTER_IDS: number[] = [
  FILTER_ID.WELLNESSCARD,
  FILTER_ID.GIFTCARD,
  FILTER_ID.PAY_LATER,
  FILTER_ID.CERTIFIED,
];

function getAppliedPrefs(type: SearchType, prefs: UrlSearchState['prefs']): UrlSearchState['prefs'] {
  if (type !== 'deals_sistaminuten') return prefs;

  return prefs
    ?.split(',')
    .filter((pref) => SISTAMINUTEN_FILTER_IDS.includes(+pref))
    .join(',');
}

export function getSearchPerformedTrackingProps({
  type,
  urlState,
  state,
  context,
  screenName,
}: {
  type: SearchType;
  urlState: UrlSearchState;
  state: DealsSearchState;
  context: SearchContext;
  screenName: Tracking.ScreenName;
}): TrackingProps {
  const properties: TrackingProps = {
    screen_name: screenName,
    search_context: context,
    search_query: urlState?.q.trim?.().length > 0 ? urlState.q : undefined,
    search_location_filter: getSearchLocationFilter(urlState),
    search_location_city: getSearchLocationCity(urlState),
    campaigns_shown: getCampaignsShown(type, state),
  };

  if (type !== 'deals_preview') {
    properties['sorted_by'] = getSortedBy(urlState);
    properties['search_filters'] = getSearchFilters(getAppliedPrefs(type, urlState.prefs));
    properties['nbr_search_results'] = state.list?.total > 0 ? state.list.total : undefined;
  }

  if (type === 'deals_preview') {
    properties['search_variant'] = 'deals_preview';
  } else if (type === 'deals_sistaminuten') {
    properties['search_variant'] = 'sistaminuten';
  } else if (type === 'brand_deals') {
    properties['search_variant'] = 'brand_deals';
  } else if (type === 'deals') {
    properties['search_variant'] = 'deals';
  }

  return properties;
}
